/**
 * `?modals=` codec — the single ordered query param that owns the modal stack
 * (central-modal-host plan, Phase 2 / decision 0.3). Grammar:
 *
 *   modals  = entry *( "," entry )
 *   entry   = kind [ ":" payload ]
 *   kind    = 1*( a-z / 0-9 / "-" )    ; registered kind token, kebab-case
 *   payload = kind-specific opaque string; the kind's toQuery/fromQuery owns its
 *             internal shape (e.g. "<id>:s=billing:t=vendor")
 *
 * Order of entries == stacking order, bottom→top. The codec is the ONLY
 * reader/writer of the param: deserialize once, in array order, assign z by
 * index — replacing the N racing per-modal `immediate: true` watchers behind the
 * refresh-ordering bug.
 *
 * Escaping: only "," (entry separator) and "%" (escape char) are reserved inside
 * a payload, percent-encoded as "%2C" / "%25". ":" and "=" pass through raw so
 * URLs stay readable; UUIDs and kebab enums never contain the reserved chars.
 */
export interface ParsedModalEntry {
  kind: string
  payload?: string
}

const ENTRY_SEP = ','
const KIND_SEP = ':'
const KIND_RE = /^[a-z0-9-]+$/

function encodePayload(value: string): string {
  return value.replace(/%/g, '%25').replace(/,/g, '%2C')
}

function decodePayload(value: string): string {
  return value.replace(/%2C|%25/gi, (m) =>
    m.toUpperCase() === '%2C' ? ',' : '%'
  )
}

/** Parse a `?modals=` value into ordered entries (bottom→top). */
export function parseModalsParam(
  raw: string | null | undefined
): ParsedModalEntry[] {
  if (!raw) return []
  const result: ParsedModalEntry[] = []
  for (const token of raw.split(ENTRY_SEP)) {
    if (!token) continue
    const sep = token.indexOf(KIND_SEP)
    const kind = sep === -1 ? token : token.slice(0, sep)
    if (!KIND_RE.test(kind)) continue
    if (sep === -1) {
      result.push({ kind })
    } else {
      result.push({ kind, payload: decodePayload(token.slice(sep + 1)) })
    }
  }
  return result
}

/** Serialize ordered entries (bottom→top) into a `?modals=` value. */
export function serializeModals(entries: ParsedModalEntry[]): string {
  return entries
    .filter((entry) => KIND_RE.test(entry.kind))
    .map((entry) =>
      entry.payload != null && entry.payload !== ''
        ? `${entry.kind}${KIND_SEP}${encodePayload(entry.payload)}`
        : entry.kind
    )
    .join(ENTRY_SEP)
}

/**
 * Merge `overrides` onto `base`, by kind: an override replaces the same-kind
 * entry (removed from its old position) and appends on top, preserving the
 * order of the rest. Used to fold legacy open-params onto the current stack.
 */
export function mergeModalEntries(
  base: ParsedModalEntry[],
  overrides: ParsedModalEntry[]
): ParsedModalEntry[] {
  const overrideKinds = new Set(overrides.map((entry) => entry.kind))
  return [
    ...base.filter((entry) => !overrideKinds.has(entry.kind)),
    ...overrides
  ]
}

/**
 * Build a payload of the form `<primary>[:<key>=<value>]*`, omitting empty
 * fields. Kinds use this for their `toQuery` (primary key first, then optional
 * short-keyed fields like `s=` section, `t=` type).
 */
export function buildModalPayload(
  primary: string,
  fields: Record<string, string | undefined>
): string {
  let payload = primary
  for (const [key, value] of Object.entries(fields)) {
    if (value != null && value !== '') payload += `:${key}=${value}`
  }
  return payload
}

/** Parse a `<primary>[:<key>=<value>]*` payload back into its parts. */
export function parseModalPayload(payload: string): {
  primary: string
  fields: Record<string, string>
} {
  const parts = payload.split(':')
  const fields: Record<string, string> = {}
  for (const part of parts.slice(1)) {
    const eq = part.indexOf('=')
    if (eq > 0) fields[part.slice(0, eq)] = part.slice(eq + 1)
  }
  return { primary: parts[0] ?? '', fields }
}
